<template>
    <ModalBase :show-modal="showModal" @close-modal="close" :auto-h="true" class="company-modal-onboarding">
        <template v-slot:titlebar>
            <TitleBar variant-class="title-modal">
                <template v-slot:pretitle>
                    {{ $gettext('Your Company') }}
                </template>
                <template v-slot:title>
                    <AnimationItem class="anim-company" :animation="{ animationData: companyAnimation.default }" :key="'anim_success'" />
                    <span v-translate>Welcome to Gekko</span>
                </template>
            </TitleBar>
        </template>
        <template v-slot:maincontent>
            <div class="onboarding-info-text" v-translate>
                We just need one more thing<br>to get your administration ready!
            </div>
                
            <b-form ref="company_form" @submit.prevent="saveCompanyDetails">
                <b-form-group>
                    <b-input-group class="company-modal-onboarding-input company-modal-onboarding-input--append">
                        <b-form-input type="text" v-model="companyDetails.name" required :placeholder="$gettext('Enter your company name')" />
                        <template #append>
                            <GekkoButton 
                                id="import-from-kvk-button"
                                v-if="getCurrentCompany.country === 'NL'"
                                @click="importFromKVK()"
                                :text="kvkButtonText" 
                                :icon="kvkButtonIcon"
                                :disabled="!isKvkButtonEnabled"
                                class="w-100"
                            />
                        </template>
                    </b-input-group>
                </b-form-group>

                <KvkSuggestionsTable 
                    v-show="showKvkSuggestionTable"
                    ref="kvk_suggestion_table"
                    :companyName="companyDetails.name"
                    @companySelected="onKvkCompanySelected"
                />
            </b-form>
            <hr v-if="companyDetailsSummary.length">
            <div class="selected-company-details" v-if="companyDetailsSummary.length">
                <div class="selected-company-details--name">{{ companyDetails.name }}</div>
                <div v-for="detail in companyDetailsSummary" :key="detail.key" :class="['selected-company-details--' + detail.key]">
                    <span v-if="detail.show_key" class="selected-company-details__label"> {{ detail.key }} </span> {{ detail.value }}
                </div>
            </div>
        </template>
        <template v-slot:actions>
            <GekkoButton 
                type="submit"
                @click="saveDetails()"
                :text="$gettext('Save')"
                icon="fa-solid fa-floppy-disk"
                class="float-right"
            />
        </template>
    </ModalBase>
</template>

<script>
import { mapGetters } from "vuex";
import ModalBase from "@/components/base_components/ModalBase";
import GekkoButton from "@/components/GekkoButton";
import TitleBar from "@/components/TitleBar";
import KvkSuggestionsTable from "@/components/contacts/KvkSuggestionsTable";
import CompanyService from "@/store/services/company.service";
// import ProfileService from "@/store/services/profile.service.js";

import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'
import * as companyAnimation from '@/css/animations/company.json';

export default {
    name: "CompanyModal",
    components: { ModalBase, GekkoButton, TitleBar, KvkSuggestionsTable, AnimationItem },
    data() {
        return {
            showModal: false,
            showKvkSuggestionTable: false,
            companyDetails: {
                name: ""
            },
            personalDetails: {
                full_name: ""
            },
            companyDetailsSummary: [],
            kvkButtonText: this.$gettext("Import from KVK"),
            kvkButtonIcon: "fa-solid fa-file-import",
            companyAnimation: companyAnimation,
        }
    },
    watch: {
        "$store.state.showCompanyModal": {
            handler: function () {
                this.showModal = true;
            },
            deep: true
        }
    },
    created() {
        window.addEventListener('keydown', this.closeEventListener);
    },
    destroyed () {
        window.removeEventListener('keydown', this.closeEventListener);
    },
    methods: {
        closeEventListener(event) {
            if (this.showModal && event.key === 'Escape') this.close();
        },
        close() {
            this.showModal = false;
        },
        onKvkDropdownClose() {
            this.showKvkSuggestionTable = false;
        },
        _setCompanyDetailsSummary() {
            this.companyDetailsSummary = [];

            if (this.companyDetails.address && this.companyDetails.address.length) {
                this.companyDetailsSummary.push({ key: "Address", value: this.companyDetails.address, show_key: false });
            }

            if (this.companyDetails.city && this.companyDetails.city.length) {
                this.companyDetailsSummary.push({ key: "City", value: this.companyDetails.city, show_key: false });
            }
            
            if (this.companyDetails.kvk && this.companyDetails.kvk.length) {
                this.companyDetailsSummary.push({ key: "KVK", value: this.companyDetails.kvk, show_key: true });
            }
            
            if (this.companyDetails.btw && this.companyDetails.btw.length) {
                this.companyDetailsSummary.push({ key: "BTW", value: this.companyDetails.btw, show_key: true });
            }
        },
        onKvkCompanySelected(kvkData) {
            this.companyDetails = CompanyService.kvkDataToCompany(kvkData).then((companyData) => {
                this.companyDetails = companyData;
                this._setCompanyDetailsSummary();
                this.onKvkDropdownClose();
                this.kvkButtonText = this.$gettext("Search");
                this.kvkButtonIcon = "fa-solid fa-pencil";
            });
        },
        importFromKVK() {
            if (this.isKvkButtonEnabled) {
                this.showKvkSuggestionTable = true;
                this.$refs.kvk_suggestion_table.getCompanyList();
                this.kvkButtonText = this.$gettext("Refresh");
                this.kvkButtonIcon = "fa-solid fa-undo";
            }
        },
        saveDetails() {
            if(!this.$refs.company_form.reportValidity()) {
                return false;
            }

            let companyDetailsCopy = JSON.parse(JSON.stringify(this.companyDetails));
            this.companyDetails = this.getCurrentCompany;

            this.companyDetails.name = companyDetailsCopy.name;
            this.companyDetails.city = companyDetailsCopy.city;
            this.companyDetails.address = companyDetailsCopy.address;
            this.companyDetails.postal_code = companyDetailsCopy.postal_code;
            this.companyDetails.kvk = companyDetailsCopy.kvk;
            this.companyDetails.btw = companyDetailsCopy.btw;
            this.companyDetails.description = companyDetailsCopy.description;
            this.companyDetails.legal_form = companyDetailsCopy.legal_form;
            this.companyDetails.sbi_code = companyDetailsCopy.sbi_code;
            this.companyDetails.sbi_description = companyDetailsCopy.sbi_description;
            
            CompanyService.update(this.companyDetails).then((response) => {
                this.$store.commit("main/SET_CURRENT_COMPANY", response.data);
                this.close();
            }).catch((error) => {
                if (error.data.name) {
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Error updating company profile"),
                        text: error.data.name,
                        variant: "danger",
                        duration: 4
                    });
                } else {
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Error"),
                        text: this.$gettext("Something went wrong..."),
                        variant: "danger",
                        duration: 4
                    });
                }
            });
        }
    },
    computed: {
        ...mapGetters({
            getCurrentCompany: "main/getCurrentCompany",
            getCurrentUser: "main/getCurrentUser"
        }),
        isKvkButtonEnabled() {
            return this.companyDetails.name && this.companyDetails.name.length > 0;
        }
    }
}
</script>

<style lang="scss" >
.company-modal-onboarding {
    .gk-modal-wrapper { 
        z-index: 1011;
        .gk-modal {
            max-width: 650px !important;
            padding: 2rem;
            .titlebar.title-modal {
                background: none;
                text-align: center;
                margin: 0 !important;
                width: 100%;
                padding-top: 0;
                padding-bottom: 0.5rem;
                .title-wrap {
                    justify-content: center;
                    .pagetitle { align-items: center; }
                    .action-bar { display: none; }
                }
            }
        }
    }
    .anim-company {
       svg {
            max-height: 240px;
            padding-bottom: 1rem;
       }
    }
}
</style>

<style lang="scss" scoped>
.company-modal-onboarding {
    .onboarding-info-text {
        color: $colorTextSemi;
        font-size: 1rem;
        padding-bottom: 2rem;
        text-align: center;
    }
    .company-modal-onboarding-input {
        border-radius: $radiusMd;
        box-shadow: $dropdownShadow;
        .form-control {
            background: white;
            height: 44px;
            box-shadow: none;
            border: 1px solid #c0c0c5;
        }
        &--append {
            .form-control {
                border-right: 0;
            }
        }
        .input-group-append {
            .btn {
                border-top-right-radius: $radiusMd !important;
                border-bottom-right-radius: $radiusMd !important;
                &.disabled {
                    background: $colorUnassigned;
                    border-color: $colorUnassigned;
                }
            }
        }
    }
    .selected-company-details {
        padding: 1rem;
        border: 1px solid #22ac8a87;
        background: #cef5eb1c;
        box-shadow: $dropdownShadow;
        border-radius: $radiusMd;
        font-size: 1rem;
        &__label {
            color: $colorUnassigned;
            font-size: 14px; 
        }
        &--name {
            font-family: DINNextLTPro-Medium, sans-serif;
            font-size: 18px;
        }
        &--City {
            font-size: 14px; 
        }
    }
}
</style>